import type { ParamSchema, ReferenceAssetInfo } from "@/lib/api";

export const CANDIDATE_COUNT = 3;
export const COUNT_PARAM_NAMES = ["num_images", "n", "output_count", "max_images"] as const;
export const REFERENCE_PARAM_NAMES = ["reference_images", "image"] as const;
export const COUNT_GATE_PARAM = "sequential_image_generation";

export interface OutputCount {
  param: string | null;
  perJob: number;
  companions: Record<string, unknown>;
}

export interface ReferenceParam {
  name: string;
  isList: boolean;
}

export interface PortraitPlan {
  params: Record<string, unknown>;
  perJob: number;
  jobCount: number;
}

function byName(params: ParamSchema[]): Map<string, ParamSchema> {
  return new Map(params.filter((param) => param?.name).map((param) => [param.name, param]));
}

export function resolveOutputCount(
  params: ParamSchema[],
  wanted: number = CANDIDATE_COUNT
): OutputCount {
  const lookup = byName(params);
  for (const name of COUNT_PARAM_NAMES) {
    const schema = lookup.get(name);
    if (!schema || schema.widget !== "number") continue;
    const low = schema.min || 1;
    const high = schema.max || low;
    const perJob = Math.max(low, Math.min(wanted, high));
    const companions: Record<string, unknown> = {};
    const gate = lookup.get(COUNT_GATE_PARAM);
    if (perJob > 1 && gate && (gate.options ?? []).some((option) => option === "auto")) {
      companions[COUNT_GATE_PARAM] = "auto";
    }
    return { param: name, perJob, companions };
  }
  return { param: null, perJob: 1, companions: {} };
}

export function resolveReferenceParam(params: ParamSchema[]): ReferenceParam | null {
  const lookup = byName(params);
  for (const name of REFERENCE_PARAM_NAMES) {
    const schema = lookup.get(name);
    if (!schema) continue;
    if (schema.widget !== "upload" && schema.widget !== "multi_upload") continue;
    return { name, isList: schema.widget === "multi_upload" };
  }
  return null;
}

export function buildPortraitPlan(
  params: ParamSchema[],
  options: { prompt: string; parentAssetId?: string | null }
): PortraitPlan {
  const built: Record<string, unknown> = {};
  for (const schema of params) {
    if (schema?.default !== undefined && schema.widget !== "hidden") {
      built[schema.name] = schema.default;
    }
  }
  built.prompt = options.prompt;
  const { param, perJob, companions } = resolveOutputCount(params);
  if (param) {
    built[param] = perJob;
    Object.assign(built, companions);
  }
  if (options.parentAssetId) {
    const reference = resolveReferenceParam(params);
    if (!reference) throw new Error("This model cannot take a reference image");
    const ref = { asset_id: options.parentAssetId };
    built[reference.name] = reference.isList ? [ref] : ref;
  }
  return { params: built, perJob, jobCount: Math.ceil(CANDIDATE_COUNT / perJob) };
}

export interface WorkingReference {
  asset_id: string;
  previewUrl: string;
  local: boolean;
  storageKey: string | null;
}

/**
 * Add one just-attached asset to the dialog's working reference list.
 *
 * Only that id is taken from the server response: uploads made in this session
 * are not yet persisted, and references removed in this session must not come
 * back from the row the server still holds.
 */
export function mergeAttachedReference(
  current: WorkingReference[],
  element: { canonical_references?: ReferenceAssetInfo[] },
  assetId: string
): WorkingReference[] {
  if (current.some((reference) => reference.asset_id === assetId)) return current;
  const resolved = (element.canonical_references ?? []).find(
    (reference) => reference.asset_id === assetId
  );
  return [
    ...current,
    {
      asset_id: assetId,
      previewUrl: "",
      local: false,
      storageKey: resolved ? resolved.thumbnail_storage_key ?? resolved.storage_key : null,
    },
  ];
}
